import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as parentOrganizationsSlice from '../../../data/parent-organizations.slice';
import { useTranslation } from 'react-i18next';
import clientUtils from '../../../services/clientUtils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { Client } from '../../../data/models/client.model';
import ProfileForm from './ProfileForm';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

const PREFIX = 'ClientProfileDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.actions}`]: {
    gap: theme.spacing(1),
  },
}));

interface ClientProfileDialogProps {
  title: string;
  open: boolean;
  client?: Client;
  close: () => void;
}

export default function ClientProfileDialog({
  title,
  open,
  client,
  close,
}: ClientProfileDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const parentOrganizations = useAppSelector(
    parentOrganizationsSlice.selectParentOrganizations,
  );
  const clientVerticals = useAppSelector(lookupSlice.selectClientVerticals);
  const savingClient = useAppSelector(clientSlice.selectSavingClient);

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: clientUtils.getProfileDefaults(client),
  });

  React.useEffect(() => {
    dispatch(parentOrganizationsSlice.fetchParentOrganizationsAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('clientVertical'));
  }, []);

  // Closing after a save is handled by ClientProfileTab: the save clears the
  // current client, which unmounts this dialog before the save completes.
  const handleClose = () => {
    formHook.reset();
    close();
  };

  const handleSave = (data: any) => {
    const clientData = clientUtils.getClientFromFormData(data, false);
    const { isInternal, ...client } = clientData;
    dispatch(clientSlice.updateClientAsync(client));
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>
        {title}
        {savingClient && (
          <LinearProgress style={{ width: '100%' }} color="primary" />
        )}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <ProfileForm
            isNew={!client}
            parentOrganizations={parentOrganizations}
            clientVerticals={clientVerticals}
            formHook={formHook}
          />
        </LocalizationProvider>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !formHook.formState.isValid ||
            !formHook.formState.isDirty ||
            savingClient
          }
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
